import { useState, useEffect, useCallback, useRef } from 'react';
import type { EnergyDashboardData, EnergyFilters } from '@/types/energy';

// Demo configuration by time range
// Shorter ranges = faster polling + bigger variance
const DEMO_CONFIG: Record<EnergyFilters['dateRange'], { interval: number; variance: number }> = {
  'today': { interval: 3000, variance: 0.06 },   // 3s, 6% variance
  '48h':   { interval: 5000, variance: 0.04 },   // 5s, 4%
  '3d':    { interval: 8000, variance: 0.03 },   // 8s, 3%
  '5d':    { interval: 10000, variance: 0.02 },  // 10s, 2%
  '7d':    { interval: 12000, variance: 0.015 }, // 12s, 1.5%
  '30d':   { interval: 20000, variance: 0.008 }, // 20s, 0.8%
  '60d':   { interval: 30000, variance: 0.005 }, // 30s, 0.5%
  '90d':   { interval: 45000, variance: 0.003 }, // 45s, 0.3%
  'production': { interval: 20000, variance: 0.008 }, // Same as 30d (variable range)
};

interface UseLiveDemoOptions {
  enabled: boolean;
  dateRange: EnergyFilters['dateRange'];
  baseData: EnergyDashboardData | null;
}

interface UseLiveDemoResult {
  data: EnergyDashboardData | null;
  isLive: boolean;
  config: { interval: number; variance: number };
}

/**
 * Apply variance to a value
 * @param value - Base value
 * @param variance - Max variance as decimal (0.06 = 6%)
 * @param direction - 'both' allows +/-, 'up' only increases
 * @param min - Minimum allowed value
 * @param max - Maximum allowed value
 */
function applyVariance(
  value: number,
  variance: number,
  direction: 'both' | 'up' = 'both',
  min: number = 0,
  max: number = Infinity
): number {
  const randomFactor = Math.random() * 2 - 1; // -1 to 1
  const change = value * variance * (direction === 'up' ? Math.abs(randomFactor) : randomFactor);
  return Math.max(min, Math.min(max, value + change));
}

/**
 * Hook to simulate live data updates for demo mode
 */
export function useLiveDemo({
  enabled,
  dateRange,
  baseData,
}: UseLiveDemoOptions): UseLiveDemoResult {
  const config = DEMO_CONFIG[dateRange] || DEMO_CONFIG['30d'];
  const [liveData, setLiveData] = useState<EnergyDashboardData | null>(baseData);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const updateIndexRef = useRef(0);

  // Reset live data when base data changes
  useEffect(() => {
    setLiveData(baseData);
  }, [baseData]);

  const generateUpdate = useCallback(() => {
    if (!liveData) return;

    const { variance } = config;
    const idx = updateIndexRef.current++;

    setLiveData((prev) => {
      if (!prev) return prev;

      const newData: EnergyDashboardData = JSON.parse(JSON.stringify(prev));

      // Rotate which KPI gets updated (not all at once for visual interest)
      const kpiIndex = idx % 5;

      switch (kpiIndex) {
        case 0:
          // Off-grid share fluctuates
          newData.kpis.offGridEnergyShare = Math.round(
            applyVariance(prev.kpis.offGridEnergyShare ?? 0, variance, 'both', 40, 100)
          );
          break;
        case 1:
          // Cost reduced only increases (cumulative)
          newData.kpis.energyCostReduced = Math.round(
            applyVariance(prev.kpis.energyCostReduced ?? 0, variance * 0.5, 'up')
          );
          break;
        case 2:
          // Diesel reduced only increases (cumulative)
          newData.kpis.dieselGallonsReduced = Math.round(
            applyVariance(prev.kpis.dieselGallonsReduced ?? 0, variance * 0.5, 'up')
          );
          break;
        case 3:
          // CO2 reduced only increases (cumulative)
          newData.kpis.totalCO2Reduced =
            Math.round(applyVariance(prev.kpis.totalCO2Reduced ?? 0, variance * 0.5, 'up') * 10) / 10;
          break;
        case 4:
          // Fully off-grid days changes less frequently
          if (Math.random() > 0.7) {
            newData.kpis.fullyOffGridDays = Math.round(
              applyVariance(prev.kpis.fullyOffGridDays ?? 0, variance * 0.3, 'both', 0)
            );
          }
          break;
      }

      // Update solar battery summary occasionally
      if (idx % 3 === 0) {
        newData.solarBattery = {
          ...prev.solarBattery,
          solarUtilization: Math.round(
            applyVariance(prev.solarBattery.solarUtilization ?? 0, variance, 'both', 50, 100)
          ),
          avgBatteryAutonomy: Math.round(
            applyVariance(prev.solarBattery.avgBatteryAutonomy ?? 0, variance * 0.5, 'both', 1, 48)
          ),
        };
      }

      // Update energy mix summary (skip when the server sent none)
      if (idx % 2 === 0 && prev.energyMixSummary) {
        const solarKwh = Math.round(
          applyVariance(prev.energyMixSummary.solarKwh, variance, 'both', 0)
        );
        const gridKwh = Math.round(
          applyVariance(prev.energyMixSummary.gridKwh, variance, 'both', 0)
        );
        const generatorKwh = Math.round(
          applyVariance(prev.energyMixSummary.generatorKwh, variance, 'both', 0)
        );

        newData.energyMixSummary = {
          solarKwh,
          gridKwh,
          generatorKwh,
          totalKwh: solarKwh + gridKwh + generatorKwh,
        };
      }

      // Update timestamp
      newData.lastUpdated = new Date().toISOString();

      return newData;
    });
  }, [liveData, config]);

  // Set up polling interval
  useEffect(() => {
    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Only poll if enabled and we have data
    if (!enabled || !baseData) {
      return;
    }

    intervalRef.current = setInterval(generateUpdate, config.interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, config.interval, generateUpdate, baseData]);

  return {
    data: enabled ? liveData : baseData,
    isLive: enabled && !!baseData,
    config,
  };
}
